/**
 * ShowcaseTemplate.tsx
 * ────────────────────────────────────────────────────────────────────────
 * Template del plan FREE de InnovaAgent. Único disponible en plan free
 * y también en starter (starter sin IA, free con IA — la validación
 * ocurre dentro de ChatWidget).
 *
 * Peculiaridades preservadas:
 *  - Limita a MAX_FREE_PROPERTIES (3) propiedades visibles.
 *  - NO usa PropertyFilters ni PropertyPagination (funciones premium).
 *  - Muestra bloque de upgrade cuando el agente tiene más propiedades.
 *
 * Identidad dual correctamente separada (NUNCA se contraponen):
 *  - Marca PERSONAL (avatar_url + name + prefix) → hero, biografía.
 *  - Imagen CORPORATIVA (logo_url + business_name) → header, footer,
 *    logo difuminado de fondo en el hero.
 *
 * Paleta: slate-900 base + indigo-600/700 acento + indigo-400 highlights.
 *
 * Plan mínimo: free
 * Privado: No
 * Refactorizado: 17 Abril 2026
 *
 * Checklist de 7 puntos:
 *  [x] 1. Interface AgentFull (importada de _shared/types)
 *  [x] 2. Flags show_* desde helpers compartidos
 *  [x] 3. Patrón <a seguro (cada <a> en su propia condición)
 *  [x] 4. Footer con documentos legales + cache + modal
 *  [x] 5. Disclaimers NOM-247 en cards de propiedades
 *  [x] 6. Sello NOM-247 (vía useNomSeal)
 *  [x] 7. SEO + Schema.org (vía setPageSEO)
 * ────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Phone,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Toilet,
  ArrowRight,
  Star,
  Lock,
  Mail,
  Clock,
  Shield,
  Menu,
  X,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
} from 'lucide-react'

import ChatWidget from '@/components/ChatWidget'
import { DASHBOARD_URL, PLATFORM_URL } from '@/constants/config'

import type {
  AgentTemplateProps,
  Property,
  LegalDocType,
} from './_shared/types'
import {
  getSubdomain,
  formatPrice,
  resolveContactChannels,
  resolveIdentityDisplay,
  resolveImageStrategy,
  resolveLegalDocs,
  resolveChatConfig,
  useNomSeal,
} from './_shared/agentHelpers'
import { setPageSEO } from './_shared/seoHelpers'
import { API_ROUTES } from './_shared/constants'

// ══════════════════════════════════════════════════════════════════════════
// Constantes del template
// ══════════════════════════════════════════════════════════════════════════
const MAX_FREE_PROPERTIES = 3

// ══════════════════════════════════════════════════════════════════════════
// Copy i18n-ready — TODOS los literales aquí (migración directa a t() futura)
// ══════════════════════════════════════════════════════════════════════════
const content = {
  kicker_default: 'Asesor Inmobiliario Profesional',
  kicker_with_brand: (brand: string) => `Asesor Inmobiliario · ${brand}`,
  hero_scroll_hint: 'Ver propiedades',
  hero_chat_fallback:
    'Conversemos directamente en el chat de esta página — estoy disponible para ti',
  hero_chat_fallback_no_ia: 'Contáctame a través de mis canales oficiales',
  nav_properties: 'Propiedades',
  nav_about: 'Conóceme',
  nav_contact: 'Contacto',
  nav_menu: 'Menú',
  nav_close: 'Cerrar',
  properties_heading: 'Mis propiedades',
  properties_empty: 'Próximamente publicaré nuevas propiedades',
  properties_count_singular: 'propiedad',
  properties_count_plural: 'propiedades',
  disclaimer_price: 'Precio sujeto a cambios sin previo aviso',
  disclaimer_images: 'Las imágenes pueden ser ilustrativas',
  construction_preventa: 'Preventa',
  construction_en_construccion: 'En construcción',
  construction_terminado: 'Terminado',
  upgrade_block_title_singular: 'propiedad disponible',
  upgrade_block_title_plural: 'propiedades disponibles',
  upgrade_block_description:
    'Actualiza tu plan para mostrar todo tu portafolio y acceder a filtros de búsqueda, paginación y más templates.',
  upgrade_block_cta: 'Ver planes disponibles',
  specialties_heading: 'Especialidades',
  zones_heading: 'Zonas de cobertura',
  cta_upgrade_heading: '¿Listo para llevar tu presencia al siguiente nivel?',
  cta_upgrade_description:
    'Con un plan de pago accedes a más templates, todo tu portafolio visible, filtros de búsqueda, analíticas y mucho más.',
  cta_upgrade_primary: 'Contáctame',
  cta_upgrade_secondary: 'Ver planes InnovAgent',
  footer_heading_contact: 'Contacto',
  footer_heading_hours: 'Horario de atención',
  footer_heading_complaints: 'Atención a quejas',
  footer_heading_legal: 'Información legal',
  footer_heading_social: 'Sígueme en redes',
  footer_powered_by: 'Construido con InnovAgent',
  footer_copyright: (brand: string) =>
    `© ${new Date().getFullYear()} ${brand}. Todos los derechos reservados.`,
  legal_modal_title: 'Documento legal',
  legal_modal_close: 'Cerrar',
  legal_aviso_privacidad: 'Aviso de Privacidad',
  legal_terminos: 'Términos y Condiciones',
  legal_derechos_consumidor: 'Derechos del Consumidor',
  legal_info_nom247: 'Información NOM-247',
}

// ══════════════════════════════════════════════════════════════════════════
// Helpers de etiquetado local (puros — fuera del componente)
// ══════════════════════════════════════════════════════════════════════════

const legalDocLabel = (docType: LegalDocType): string => {
  switch (docType) {
    case 'aviso_privacidad':
      return content.legal_aviso_privacidad
    case 'terminos_condiciones':
      return content.legal_terminos
    case 'derechos_consumidor':
      return content.legal_derechos_consumidor
    case 'info_nom247':
      return content.legal_info_nom247
  }
}

const constructionLabel = (
  status: NonNullable<Property['construction_status']>
): string => {
  switch (status) {
    case 'preventa':
      return content.construction_preventa
    case 'en_construccion':
      return content.construction_en_construccion
    case 'terminado':
      return content.construction_terminado
  }
}

// SVG de WhatsApp — componente mínimo para reutilizar sin repetir path
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

// ══════════════════════════════════════════════════════════════════════════
// Componente principal
// ══════════════════════════════════════════════════════════════════════════

const ShowcaseTemplate: React.FC<AgentTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()

  // ── Helpers resueltos ────────────────────────────────────────────────
  const contact = resolveContactChannels(agent)
  const identity = resolveIdentityDisplay(agent)
  const images = resolveImageStrategy(agent)
  const legal = resolveLegalDocs(agent)
  const chat = resolveChatConfig(agent)
  const nomSeal = useNomSeal(agent)

  // Nombre "cabeza" para el copyright del footer
  const brandForCopyright =
    identity.corporate.show && identity.corporate.name
      ? identity.corporate.name
      : identity.personal.show
        ? identity.personal.name
        : agent.name

  // Kicker del hero: si hay marca corporativa visible, se usa como contexto.
  const heroKicker = identity.corporate.show
    ? content.kicker_with_brand(identity.corporate.name)
    : content.kicker_default

  // ── Estado ───────────────────────────────────────────────────────────
  const [properties, setProperties] = useState<Property[]>([])
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [legalModal, setLegalModal] = useState<{
    open: boolean
    type: LegalDocType | null
    html: string
  }>({ open: false, type: null, html: '' })
  const legalDocsCache = useRef<Record<string, string>>({})

  // ── Fetch propiedades ────────────────────────────────────────────────
  useEffect(() => {
    const subdomain = getSubdomain()
    fetch(API_ROUTES.publicProperties(subdomain))
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Property[]) => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
  }, [])

  // ── SEO ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setPageSEO(agent, {
      ogImage: images.personal.url ?? images.corporate.url ?? undefined,
    })
  }, [agent, images.personal.url, images.corporate.url])

  // ── Derivados ────────────────────────────────────────────────────────
  const visibleProperties = properties.slice(0, MAX_FREE_PROPERTIES)
  const hiddenCount = Math.max(0, properties.length - MAX_FREE_PROPERTIES)

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  // ── Docs legales: fetch con cache en memoria ─────────────────────────
  const openLegalDoc = async (docType: LegalDocType) => {
    if (legalDocsCache.current[docType]) {
      setLegalModal({
        open: true,
        type: docType,
        html: legalDocsCache.current[docType],
      })
      return
    }
    try {
      const subdomain = getSubdomain()
      const res = await fetch(API_ROUTES.legalDocPreview(docType, subdomain))
      const data = await res.json()
      legalDocsCache.current[docType] = data.html
      setLegalModal({ open: true, type: docType, html: data.html })
    } catch {
      // Silencio consciente: botón no abre modal si falla la red
    }
  }

  const closeLegalModal = () =>
    setLegalModal({ open: false, type: null, html: '' })

  // ── Redes sociales (lista filtrada) ──────────────────────────────────
  const social = agent.social_media ?? {}
  const socialLinks: { icon: typeof Facebook; url: string; label: string }[] = []
  if (social.facebook) socialLinks.push({ icon: Facebook, url: social.facebook, label: 'Facebook' })
  if (social.instagram) socialLinks.push({ icon: Instagram, url: social.instagram, label: 'Instagram' })
  if (social.linkedin) socialLinks.push({ icon: Linkedin, url: social.linkedin, label: 'LinkedIn' })
  if (social.youtube) socialLinks.push({ icon: Youtube, url: social.youtube, label: 'YouTube' })

  // Header solo aparece si hay algo que anclar visualmente
  const showHeader =
    images.corporate.show || identity.corporate.show || identity.personal.show

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="relative min-h-screen bg-white font-sans text-slate-900">

      {/* ══ SELLO NOM-247 (top-right, responsive, fixed) ══ */}
      {nomSeal.visible && (
        <a
          href={nomSeal.verifyUrl!}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={nomSeal.copy.ariaLabel}
          title={nomSeal.copy.tooltip}
          className="fixed top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 z-40 group"
        >
          <img
            src={nomSeal.assetPath}
            alt={nomSeal.copy.altText}
            className="h-10 sm:h-14 lg:h-16 xl:h-20 w-auto drop-shadow-lg group-hover:scale-105 transition-transform duration-300"
          />
          <span className="sr-only">{nomSeal.copy.linkText}</span>
        </a>
      )}

      {/* ══ HEADER (imagen corporativa) ══ */}
      {showHeader && (
        <header className="absolute top-0 left-0 right-0 z-30">
          <nav className="container mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
            {/* Identidad corporativa o fallback personal */}
            <div className="flex items-center gap-3 min-w-0">
              {images.corporate.show && (
                <img
                  src={images.corporate.url!}
                  alt={identity.corporate.show ? identity.corporate.name : 'Logo'}
                  className="h-9 sm:h-10 w-auto object-contain shrink-0"
                />
              )}
              {identity.corporate.show && (
                <span className="text-white font-semibold text-base sm:text-lg tracking-tight truncate">
                  {identity.corporate.name}
                </span>
              )}
              {!images.corporate.show &&
                !identity.corporate.show &&
                identity.personal.show && (
                  <span className="text-white font-semibold text-base sm:text-lg tracking-tight truncate">
                    {identity.personal.name}
                  </span>
                )}
            </div>

            {/* Nav desktop */}
            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-indigo-100">
              <a href="#properties" className="hover:text-white transition-colors">
                {content.nav_properties}
              </a>
              <a href="#about" className="hover:text-white transition-colors">
                {content.nav_about}
              </a>
              <a
                href="#contact"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
              >
                {content.nav_contact}
              </a>
            </div>

            {/* Botón hamburguesa móvil */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label={content.nav_menu}
            >
              <Menu size={22} />
            </button>
          </nav>

          {/* Panel nav móvil */}
          {mobileNavOpen && (
            <div className="md:hidden fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm flex flex-col">
              <div className="flex justify-between items-center px-4 py-4 border-b border-white/10">
                <span className="text-white font-semibold text-lg">{content.nav_menu}</span>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label={content.nav_close}
                >
                  <X size={22} />
                </button>
              </div>
              <div className="flex flex-col gap-5 px-6 pt-8 text-lg text-indigo-100">
                <a
                  href="#properties"
                  onClick={() => setMobileNavOpen(false)}
                  className="hover:text-white"
                >
                  {content.nav_properties}
                </a>
                <a
                  href="#about"
                  onClick={() => setMobileNavOpen(false)}
                  className="hover:text-white"
                >
                  {content.nav_about}
                </a>
                <a
                  href="#contact"
                  onClick={() => setMobileNavOpen(false)}
                  className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition-colors"
                >
                  {content.nav_contact}
                </a>
              </div>
            </div>
          )}
        </header>
      )}

      {/* ══ HERO (marca PERSONAL protagonista) ══ */}
      <section
        id="about"
        className="relative min-h-screen bg-slate-900 overflow-hidden flex items-center"
      >
        {/* Fondo decorativo: orbes indigo + grid sutil + logo difuminado */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[520px] h-[520px] bg-indigo-600/15 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[420px] h-[420px] bg-indigo-500/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          />
          {images.corporate.show && (
            <img
              src={images.corporate.url!}
              alt=""
              aria-hidden="true"
              className="absolute bottom-8 right-8 w-56 h-56 lg:w-64 lg:h-64 object-contain opacity-[0.05]"
            />
          )}
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 py-24 lg:py-32">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">

            {/* ── Columna texto ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left space-y-6 sm:space-y-8 order-2 lg:order-1"
            >
              {/* Kicker */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-400/30 rounded-full"
              >
                <Star size={14} className="text-indigo-300" />
                <span className="text-indigo-200 text-xs sm:text-sm font-medium tracking-wide">
                  {heroKicker}
                </span>
              </motion.div>

              {/* Título: nombre PERSONAL con prefix incluido */}
              {identity.personal.show && (
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.7 }}
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.05] tracking-tight"
                >
                  {identity.personal.displayName}
                </motion.h1>
              )}

              {/* Fallback: si no hay personal pero sí corporativa */}
              {!identity.personal.show && identity.corporate.show && (
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.7 }}
                  className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.05] tracking-tight"
                >
                  {identity.corporate.name}
                </motion.h1>
              )}

              {/* Bio */}
              {agent.bio && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-base sm:text-lg text-indigo-100/80 leading-relaxed max-w-xl mx-auto lg:mx-0"
                >
                  {agent.bio}
                </motion.p>
              )}

              {/* CTAs — cada <a> en condición independiente */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                id="contact"
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start pt-2"
              >
                {contact.showPhone && (
                  <a
                    href={`tel:${contact.phoneDigits}`}
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-indigo-600/30"
                  >
                    <Phone size={18} />
                    <span className="truncate max-w-[200px]">{contact.phoneNumber}</span>
                  </a>
                )}
                {contact.showWhatsapp && (
                  <a
                    href={`https://wa.me/${contact.whatsappDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-emerald-500/20"
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    WhatsApp
                  </a>
                )}
                {contact.showEmail && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl border border-white/10 transition-all duration-300"
                  >
                    <Mail size={18} />
                    Email
                  </a>
                )}
              </motion.div>

              {/* Fallback si no hay canales de contacto */}
              {!contact.hasAnyChannel && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="text-indigo-200/70 text-sm sm:text-base max-w-md mx-auto lg:mx-0 border-l-2 border-indigo-400/40 pl-4"
                >
                  {chat.showIA
                    ? content.hero_chat_fallback
                    : content.hero_chat_fallback_no_ia}
                </motion.p>
              )}

              {/* Zonas breves */}
              {agent.zones && agent.zones.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="flex items-center gap-2 justify-center lg:justify-start text-indigo-200/70 pt-2"
                >
                  <MapPin size={16} className="text-indigo-400 shrink-0" />
                  <span className="text-xs sm:text-sm truncate">
                    {agent.zones.slice(0, 3).join(' · ')}
                  </span>
                </motion.div>
              )}
            </motion.div>

            {/* ── Columna avatar (marca personal) ── */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex justify-center lg:justify-end order-1 lg:order-2"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-full blur-2xl opacity-25" />

                {images.personal.show ? (
                  <img
                    src={images.personal.url!}
                    alt={identity.personal.displayName}
                    className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover border-4 border-indigo-400/30 shadow-2xl"
                  />
                ) : (
                  <div className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-900 flex items-center justify-center border-4 border-indigo-400/30 shadow-2xl">
                    <span className="text-6xl sm:text-7xl lg:text-8xl font-black text-white/30 tracking-tight">
                      {images.personal.initialsFallback || agent.name.charAt(0)}
                    </span>
                  </div>
                )}

                {/* Badge de propiedades flotante */}
                {properties.length > 0 && (
                  <motion.div
                    animate={{ y: [-6, 6, -6] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -bottom-4 -right-4 bg-white rounded-2xl px-4 py-3 shadow-xl border border-indigo-100"
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-700">
                        {properties.length}
                      </div>
                      <div className="text-xs text-indigo-500 font-medium">
                        {properties.length === 1
                          ? content.properties_count_singular
                          : content.properties_count_plural}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll hint desktop */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-indigo-300/50 pointer-events-none"
        >
          <span className="text-[10px] tracking-[0.2em] uppercase">
            {content.hero_scroll_hint}
          </span>
          <div className="w-px h-8 bg-gradient-to-b from-indigo-300/50 to-transparent" />
        </motion.div>
      </section>

      {/* ══ PROPIEDADES (máx 3 en plan free) ══ */}
      <section id="properties" className="py-16 sm:py-20 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-14"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 tracking-tight">
              {content.properties_heading}
            </h2>
            <div className="w-16 h-1 bg-indigo-600 mx-auto rounded-full" />
          </motion.div>

          {visibleProperties.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
              {visibleProperties.map((property, index) => (
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.12, duration: 0.6 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title || property.id)}
                  className="group cursor-pointer bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100"
                >
                  {/* Imagen */}
                  <div className="relative h-56 overflow-hidden bg-slate-100">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center">
                        <span className="text-5xl text-indigo-300">🏠</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-indigo-700 capitalize">
                      {property.purpose}
                    </div>
                    <div className="absolute top-3 right-3 bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                      {formatPrice(property.price, property.currency)}
                    </div>
                    {property.construction_status && (
                      <div className="absolute bottom-3 left-3 bg-slate-900/90 text-white text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full font-semibold">
                        {constructionLabel(property.construction_status)}
                      </div>
                    )}
                  </div>

                  {/* Contenido */}
                  <div className="p-5">
                    <h3 className="font-bold text-slate-900 text-lg mb-1 group-hover:text-indigo-600 transition-colors line-clamp-1">
                      {property.title}
                    </h3>
                    <p className="text-indigo-600 text-sm flex items-center gap-1 mb-3">
                      <MapPin size={13} />
                      {property.zone}
                    </p>
                    <div className="flex items-center flex-wrap gap-4 text-slate-500 text-sm border-t border-slate-100 pt-3">
                      {property.bedrooms != null && property.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bed size={14} /> {property.bedrooms}
                        </span>
                      )}
                      {property.bathrooms != null && property.bathrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bath size={14} /> {property.bathrooms}
                        </span>
                      )}
                      {(property.half_bathrooms ?? 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <Toilet size={14} /> {property.half_bathrooms}
                        </span>
                      )}
                      {property.area_m2 != null && (
                        <span className="flex items-center gap-1">
                          <Maximize size={14} /> {Number(property.area_m2)}m²
                        </span>
                      )}
                    </div>

                    {/* Disclaimers NOM-247 */}
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-0.5">
                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {content.disclaimer_price}
                      </p>
                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {content.disclaimer_images}
                      </p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 text-base sm:text-lg py-8">
              {content.properties_empty}
            </p>
          )}

          {/* Bloque de upgrade cuando hay propiedades ocultas */}
          {hiddenCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mt-10 sm:mt-12 max-w-2xl mx-auto"
            >
              <div className="relative bg-white rounded-2xl border-2 border-dashed border-indigo-200 p-8 sm:p-10 text-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-white/60 to-white/95 backdrop-blur-sm pointer-events-none" />
                <div className="relative z-10 space-y-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                    <Lock size={24} className="text-indigo-500" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    +{hiddenCount}{' '}
                    {hiddenCount === 1
                      ? content.upgrade_block_title_singular
                      : content.upgrade_block_title_plural}
                  </h3>
                  <p className="text-slate-500 text-sm max-w-sm mx-auto">
                    {content.upgrade_block_description}
                  </p>
                  <a
                    href={DASHBOARD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-all duration-300 shadow-md shadow-indigo-200"
                  >
                    {content.upgrade_block_cta}
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ══ ESPECIALIDADES + ZONAS ══ */}
      {((agent.specialties && agent.specialties.length > 0) ||
        (agent.zones && agent.zones.length > 0)) && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16">

              {agent.specialties && agent.specialties.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
                    {content.specialties_heading}
                  </h2>
                  <div className="w-12 h-1 bg-indigo-600 rounded-full mb-6" />
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {agent.specialties.map((specialty) => (
                      <span
                        key={specialty}
                        className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full font-medium border border-indigo-100 text-xs sm:text-sm"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {agent.zones && agent.zones.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
                    {content.zones_heading}
                  </h2>
                  <div className="w-12 h-1 bg-indigo-600 rounded-full mb-6" />
                  <ul className="space-y-2">
                    {agent.zones.map((zone) => (
                      <li
                        key={zone}
                        className="flex items-center gap-2 text-slate-700 text-sm sm:text-base"
                      >
                        <MapPin size={16} className="text-indigo-500 shrink-0" />
                        {zone}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ CTA UPGRADE ══ */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-900 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-600/20 via-transparent to-transparent pointer-events-none" />
        <div className="relative container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
              {content.cta_upgrade_heading}
            </h2>
            <p className="text-indigo-200 text-base sm:text-lg leading-relaxed">
              {content.cta_upgrade_description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
              {contact.showPhone && (
                <a
                  href={`tel:${contact.phoneDigits}`}
                  className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-indigo-700 font-bold rounded-xl transition-all duration-300 shadow-lg hover:bg-indigo-50"
                >
                  <Phone size={18} />
                  {content.cta_upgrade_primary}
                </a>
              )}
              <a
                href={PLATFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-indigo-500/20 hover:bg-indigo-500/30 text-white font-semibold rounded-xl border border-indigo-400/30 transition-all duration-300"
              >
                {content.cta_upgrade_secondary}
                <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ FOOTER (imagen corporativa) ══ */}
      <footer className="bg-slate-900 text-indigo-100">
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">

            {/* Col 1: Identidad corporativa */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {images.corporate.show && (
                  <img
                    src={images.corporate.url!}
                    alt={identity.corporate.show ? identity.corporate.name : 'Logo'}
                    className="h-10 w-auto object-contain"
                  />
                )}
                {identity.corporate.show && (
                  <span className="text-white font-semibold text-base tracking-tight">
                    {identity.corporate.name}
                  </span>
                )}
                {!images.corporate.show &&
                  !identity.corporate.show &&
                  identity.personal.show && (
                    <span className="text-white font-semibold text-base tracking-tight">
                      {identity.personal.displayName}
                    </span>
                  )}
              </div>
              {agent.bio && (
                <p className="text-indigo-200/70 text-sm leading-relaxed line-clamp-4">
                  {agent.bio}
                </p>
              )}
            </div>

            {/* Col 2: Contacto */}
            {(contact.showPhone ||
              contact.showWhatsapp ||
              contact.showEmail ||
              contact.showAddress) && (
              <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                  {content.footer_heading_contact}
                </h3>
                <ul className="space-y-3 text-sm">
                  {contact.showPhone && (
                    <li>
                      <a
                        href={`tel:${contact.phoneDigits}`}
                        className="flex items-center gap-2 text-indigo-200/80 hover:text-white transition-colors"
                      >
                        <Phone size={14} className="shrink-0 text-indigo-400" />
                        <span className="truncate">{contact.phoneNumber}</span>
                      </a>
                    </li>
                  )}
                  {contact.showWhatsapp && (
                    <li>
                      <a
                        href={`https://wa.me/${contact.whatsappDigits}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-indigo-200/80 hover:text-white transition-colors"
                      >
                        <WhatsAppIcon className="w-3.5 h-3.5 shrink-0 text-indigo-400" />
                        WhatsApp
                      </a>
                    </li>
                  )}
                  {contact.showEmail && (
                    <li>
                      <a
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-2 text-indigo-200/80 hover:text-white transition-colors"
                      >
                        <Mail size={14} className="shrink-0 text-indigo-400" />
                        <span className="truncate">{contact.email}</span>
                      </a>
                    </li>
                  )}
                  {contact.showAddress && (
                    <li className="flex items-start gap-2 text-indigo-200/80">
                      <MapPin size={14} className="shrink-0 text-indigo-400 mt-0.5" />
                      <span>{contact.address}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Col 3: Horarios y quejas (NOM-247) */}
            {(contact.showAttentionHours || contact.showComplaintsContact) && (
              <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                  {content.footer_heading_hours}
                </h3>
                <ul className="space-y-3 text-sm">
                  {contact.showAttentionHours && (
                    <li className="flex items-start gap-2 text-indigo-200/80">
                      <Clock size={14} className="shrink-0 text-indigo-400 mt-0.5" />
                      <span>{contact.attentionHours}</span>
                    </li>
                  )}
                  {contact.showComplaintsContact && (
                    <li className="flex items-start gap-2 text-indigo-200/80">
                      <Shield size={14} className="shrink-0 text-indigo-400 mt-0.5" />
                      <span>
                        <span className="block text-white text-xs font-semibold mb-0.5">
                          {content.footer_heading_complaints}
                        </span>
                        {contact.complaintsContact}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Col 4: Legal + Redes */}
            <div className="space-y-6">
              {legal.hasAnyDoc && (
                <div>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                    {content.footer_heading_legal}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {legal.availableDocs.map((docType) => (
                      <li key={docType}>
                        <button
                          type="button"
                          onClick={() => openLegalDoc(docType)}
                          className="text-indigo-200/80 hover:text-white transition-colors text-left"
                        >
                          {legalDocLabel(docType)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {socialLinks.length > 0 && (
                <div>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                    {content.footer_heading_social}
                  </h3>
                  <div className="flex items-center gap-3">
                    {socialLinks.map(({ icon: Icon, url, label }) => (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="w-9 h-9 rounded-full bg-white/5 hover:bg-indigo-600 flex items-center justify-center text-indigo-200 hover:text-white transition-all duration-300"
                      >
                        <Icon size={16} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Barra inferior */}
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-indigo-200/60">
            <p className="text-center sm:text-left">
              {content.footer_copyright(brandForCopyright)}
            </p>
            <a
              href={PLATFORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              {content.footer_powered_by}
            </a>
          </div>
        </div>
      </footer>

      {/* ══ MODAL DOCUMENTO LEGAL ══ */}
      {legalModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={closeLegalModal}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 sm:p-5 border-b border-slate-200">
              <h3 className="font-semibold text-sm sm:text-base text-slate-900">
                {legalModal.type
                  ? legalDocLabel(legalModal.type)
                  : content.legal_modal_title}
              </h3>
              <button
                type="button"
                onClick={closeLegalModal}
                aria-label={content.legal_modal_close}
                className="text-slate-500 hover:text-slate-900 transition-colors p-1 rounded-full hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
            <div
              className="overflow-y-auto p-5 sm:p-6 text-sm leading-relaxed text-slate-700"
              dangerouslySetInnerHTML={{ __html: legalModal.html }}
            />
          </div>
        </div>
      )}

      {/* ══ CHAT WIDGET — siempre renderizado; el widget valida plan internamente ══ */}
      <ChatWidget
        agentName={agent.name}
        agentSubdomain={getSubdomain()}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default ShowcaseTemplate
